import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Copy, Trash2, LayoutTemplate, UserPlus, ChevronDown, ChevronUp } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Panel, PageTitle, StateBadge, TypeBadge, StarDivider, STATE_LABELS } from "@/components/common";
import { ManualRegister } from "@/components/ManualRegister";
import { toast } from "sonner";

const NEW = {
  name: "",
  type: "SAF",
  kd_requirement: "none",
  require_kd: false,
  kd_mode: "battle_royale",
  mode: "battle_royale",
  map: "Verdansk",
  season: "",
  start_date: "",
  start_time: "",
  max_hostable_lobbies: 1,
  num_matches: 6,
  min_players: 3,
  max_players: 3,
  championship_ids: [],
};

export default function Tornei() {
  const [tours, setTours] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [championships, setChampionships] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(NEW);
  const [manualTour, setManualTour] = useState(null);
  const navigate = useNavigate();

  const activeTours = tours.filter((t) => t.state !== "archived");
  const archivedTours = tours.filter((t) => t.state === "archived");
  const [expandedArchived, setExpandedArchived] = useState([]);

  const load = () => {
    api.get("/tournaments", { params: { templates: false } }).then(({ data }) => setTours(data));
    api.get("/tournaments", { params: { templates: true } }).then(({ data }) => setTemplates(data));
    api.get("/championships").then(({ data }) => setChampionships(data));
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Il nome del torneo è obbligatorio"); return; }
    if (!form.type) { toast.error("La tipologia torneo è obbligatoria"); return; }
    if (!form.start_date) { toast.error("La data del torneo è obbligatoria"); return; }
    if (!form.start_time) { toast.error("L'orario del torneo è obbligatorio"); return; }
    if (Number(form.max_hostable_lobbies) < 1) {
      toast.error("Il numero massimo di lobby hostabili deve essere almeno 1");
      return;
    }
    try {
      const payload = {
        ...form,
        championship_ids: form.championship_ids || [],
        championship_id: form.championship_ids?.[0] || null,
        // Compatibilità temporanea con i tornei legacy:
        // kd_requirement è la source of truth.
        require_kd: ["indicative", "verified"].includes(
          form.kd_requirement
        ),
      };

      await api.post("/tournaments", payload);
      toast.success("Torneo creato");
      setOpen(false); setForm(NEW); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const changeState = async (id, state) => {
    await api.put(`/tournaments/${id}/state`, { state });
    toast.success("Stato aggiornato"); load();
  };
  const duplicate = async (id) => { await api.post(`/tournaments/${id}/duplicate`); toast.success("Duplicato"); load(); };
  const remove = async (id) => { await api.delete(`/tournaments/${id}`); toast.success("Eliminato"); load(); };
  const genLobbies = async (id) => {
    try { const { data } = await api.post(`/tournaments/${id}/generate-lobbies`); toast.success(`${data.length} lobby generate`); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const field = (k, label, type = "text") => (
    <div>
      <label className="font-rajdhani uppercase text-[10px] tracking-widest text-[var(--silver)]">{label}</label>
      <input type={type} value={form[k]} onChange={(e) => setForm({ ...form, [k]: type === "number" ? +e.target.value : e.target.value })}
        className="w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white focus:border-[var(--gold)] focus:outline-none" data-testid={`tour-field-${k}`} />
    </div>
  );

  return (
    <div>
      <PageTitle title="Tornei" subtitle="Gestione & Template"
        action={<button data-testid="new-tournament-btn" onClick={() => setOpen(!open)} className="btn-gold clip-beveled px-6 py-2.5 flex items-center gap-2"><Plus className="w-4 h-4" /> Nuovo Torneo</button>} />

      {open && (
        <Panel gold className="p-6 mb-6 boot-in" testid="tour-form">
          <StarDivider label="Nuovo Torneo" className="mb-4" />
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="font-rajdhani uppercase text-[10px] tracking-widest text-[var(--silver)]">Tipologia Torneo *</label>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="tour-field-type"
                className="w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white focus:border-[var(--gold)] focus:outline-none">
                <option value="SAF">Torneo SAF</option>
                <option value="INTERNAL_SEALS">Torneo interno Seals</option>
              </select>
            </div>
            {field("name", "Nome")}
            {field("season", "Stagione")}
            <div>
              <label className="font-rajdhani uppercase text-[10px] tracking-widest text-[var(--silver)]">
                Campionati
              </label>
              <div
                className="mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 space-y-2"
                data-testid="tour-field-championship_ids"
              >
                {championships
                  .filter((championship) => ["draft", "active"].includes(championship.state))
                  .map((championship) => {
                    const checked = (form.championship_ids || []).includes(championship.id);

                    return (
                      <label
                        key={championship.id}
                        className="flex items-center gap-2 font-rajdhani text-sm text-white cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() =>
                            setForm({
                              ...form,
                              championship_ids: checked
                                ? form.championship_ids.filter((id) => id !== championship.id)
                                : [...form.championship_ids, championship.id],
                            })
                          }
                        />
                        <span>{championship.name}</span>
                      </label>
                    );
                  })}

                {championships.filter((championship) =>
                  ["draft", "active"].includes(championship.state)
                ).length === 0 && (
                  <p className="font-rajdhani text-xs text-[var(--silver)]">
                    Nessun Campionato associabile
                  </p>
                )}
              </div>
            </div>
            <div>
              <label className="font-rajdhani uppercase text-[10px] tracking-widest text-[var(--silver)]">
                Modalità
              </label>
              <select
                value={form.mode}
                onChange={(e) =>
                  setForm({
                    ...form,
                    mode: e.target.value,
                    kd_mode: e.target.value,
                  })
                }
                data-testid="tour-field-mode"
                className="w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white focus:border-[var(--gold)] focus:outline-none"
              >
                <option value="battle_royale">Battle Royale</option>
                <option value="resurgence">Ritorno</option>
              </select>
            </div>
            {field("start_date", "Data", "date")}
            {field("start_time", "Orario", "time")}
            {field("map", "Mappa")}
            {field("max_hostable_lobbies", "Numero massimo di lobby hostabili", "number")}
            {field("num_matches", "N. Partite", "number")}
            {field("min_players", "Min Giocatori", "number")}
            {field("max_players", "Max Giocatori", "number")}
            <div>
              <label className="font-rajdhani uppercase text-[10px] tracking-widest text-[var(--silver)]">
                Gestione K/D
              </label>

              <select
                value={form.kd_requirement}
                onChange={(e) => {
                  const kdRequirement = e.target.value;

                  setForm({
                    ...form,
                    kd_requirement: kdRequirement,
                    require_kd: ["indicative", "verified"].includes(
                      kdRequirement
                    ),
                  });
                }}
                className="w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white focus:border-[var(--gold)] focus:outline-none"
                data-testid="tour-field-kd_requirement"
              >
                <option value="none">Nessun K/D</option>
                <option value="indicative">K/D indicativo</option>
                <option value="verified">K/D verificato</option>
              </select>

              <p className="font-rajdhani text-[11px] text-[var(--silver)] mt-1">
                {form.kd_requirement === "none" &&
                  "Il K/D non viene richiesto e non influenza la composizione delle lobby."}
                {form.kd_requirement === "indicative" &&
                  "Il K/D viene dichiarato dal giocatore e può essere usato per il bilanciamento delle lobby."}
                {form.kd_requirement === "verified" &&
                  "Il K/D richiede verifica manuale tramite screenshot prima dell'approvazione."}
              </p>
            </div>

          </div>
          <div className="flex gap-3 mt-4">
            <button data-testid="save-tournament-btn" onClick={create} className="btn-gold clip-beveled px-6 py-2">Crea</button>
            <button onClick={() => setOpen(false)} className="btn-outline-gold clip-beveled px-6 py-2">Annulla</button>
          </div>
        </Panel>
      )}

      <div className="grid md:grid-cols-2 gap-4 mb-8">
        {activeTours.map((t) => (
          <Panel key={t.id} className="p-5" testid={`tournament-${t.id}`}>
            <div className="flex justify-between items-start">
              <div>
                <h3 className="heading text-3xl gold-text">{t.name}</h3>
                <p className="font-rajdhani text-xs text-[var(--silver)] uppercase tracking-widest">
                  {t.mode === "resurgence" ? "Ritorno" : "Battle Royale"}
                  {t.season ? ` · ${t.season}` : ""}
                </p>
                <div className="mt-2 flex flex-wrap gap-2 items-center">
                  <TypeBadge type={t.type} />
                  {(t.championship_ids?.length
                    ? t.championship_ids
                    : t.championship_id
                      ? [t.championship_id]
                      : []
                  ).map((championshipId) => (
                    <span
                      key={championshipId}
                      className="px-2 py-0.5 text-[10px] uppercase font-rajdhani border border-[var(--gold)]/40 text-[var(--gold)] clip-beveled-sm"
                    >
                      {championships.find((championship) => championship.id === championshipId)?.name || "Campionato"}
                    </span>
                  ))}
                </div>
              </div>
              <StateBadge state={t.state} />
            </div>
            <div className="flex gap-4 mt-3 font-rajdhani text-xs text-[var(--silver)] uppercase">
              <span>{t.map}</span>
              <span>{t.max_hostable_lobbies || 1} lobby hostabili</span>
              <span>Capienza iscrizioni: {t.max_teams} squadre</span>
              <span>{t.num_matches} partite</span>
            </div>
            <div className="flex flex-wrap gap-2 mt-4 items-center">
              <select value={t.state} onChange={(e) => changeState(t.id, e.target.value)} data-testid={`state-select-${t.id}`}
                className="bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-2 py-1.5 text-xs text-white focus:border-[var(--gold)] focus:outline-none font-rajdhani">
                {Object.entries(STATE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <button onClick={() => genLobbies(t.id)} data-testid={`gen-lobbies-${t.id}`} className="btn-outline-gold clip-beveled-sm px-3 py-1.5 text-xs">Genera Lobby</button>
              <button onClick={() => setManualTour(t)} data-testid={`manual-register-${t.id}`} className="btn-outline-gold clip-beveled-sm px-3 py-1.5 text-xs flex items-center gap-1"><UserPlus className="w-3.5 h-3.5" /> Iscrivi Squadra</button>
              <button onClick={() => navigate("/dashboard/impostazioni", { state: { tid: t.id } })} data-testid={`config-${t.id}`} className="btn-outline-gold clip-beveled-sm px-3 py-1.5 text-xs">Config</button>
              <button onClick={() => duplicate(t.id)} className="text-[var(--gold)] hover:text-[var(--gold-light)] p-1.5"><Copy className="w-4 h-4" /></button>
              <button onClick={() => remove(t.id)} data-testid={`delete-tour-${t.id}`} className="text-[var(--destructive)] hover:opacity-80 p-1.5"><Trash2 className="w-4 h-4" /></button>
            </div>
          </Panel>
        ))}
      </div>

      <div className="mb-8" data-testid="archived-tournaments">
        <StarDivider label={`Archivio Tornei (${archivedTours.length})`} className="mb-4" />
        <Panel className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left font-rajdhani text-sm">
              <thead className="bg-[#111] text-[var(--gold)] uppercase tracking-wider">
                <tr>
                  <th className="p-3">Torneo</th>
                  <th className="p-3">Data</th>
                  <th className="p-3">Modalità</th>
                  <th className="p-3">Tipologia</th>
                  <th className="p-3">Stato</th>
                  <th className="p-3 text-right">Dettagli</th>
                </tr>
              </thead>
              <tbody>
                {archivedTours.map((t) => {
                  const expanded = expandedArchived.includes(t.id);
                  return (
                    <Fragment key={t.id}>
                      <tr className="border-t border-[var(--steel)]/30 hover:bg-white/5">
                        <td className="p-3 text-white font-semibold">{t.name}</td>
                        <td className="p-3 text-[var(--silver)]">{t.start_date || "—"}</td>
                        <td className="p-3 text-[var(--silver)]">{t.mode === "resurgence" ? "Ritorno" : "Battle Royale"}</td>
                        <td className="p-3"><TypeBadge type={t.type} /></td>
                        <td className="p-3"><StateBadge state={t.state} /></td>
                        <td className="p-3 text-right">
                          <button
                            type="button"
                            aria-expanded={expanded}
                            onClick={() => setExpandedArchived((prev) =>
                              expanded ? prev.filter((id) => id !== t.id) : [...prev, t.id]
                            )}
                            className="btn-outline-gold clip-beveled-sm px-3 py-1.5 text-xs inline-flex items-center gap-1"
                          >
                            {expanded ? "Chiudi" : "Espandi"}
                            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                        </td>
                      </tr>
                      {expanded && (
                        <tr className="bg-[#0d0d0d] border-t border-[var(--steel)]/20">
                          <td colSpan={6} className="p-4">
                            <div className="flex flex-wrap gap-4 text-xs font-rajdhani text-[var(--silver)] uppercase mb-3">
                              <span>Mappa: {t.map || "—"}</span>
                              <span>{t.max_hostable_lobbies || 1} lobby hostabili</span>
                              <span>Capienza: {t.max_teams ?? "—"} squadre</span>
                              <span>{t.num_matches} partite</span>
                              <span>Orario: {t.start_time || "—"}</span>
                              {t.season && <span>Stagione: {t.season}</span>}
                            </div>
                            <div className="flex flex-wrap gap-2 items-center">
                              <select
                                value={t.state}
                                onChange={(e) => changeState(t.id, e.target.value)}
                                data-testid={`state-select-${t.id}`}
                                className="bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-2 py-1.5 text-xs text-white font-rajdhani"
                              >
                                {Object.entries(STATE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                              </select>
                              <button onClick={() => genLobbies(t.id)} data-testid={`gen-lobbies-${t.id}`} className="btn-outline-gold clip-beveled-sm px-3 py-1.5 text-xs">Genera Lobby</button>
                              <button onClick={() => setManualTour(t)} data-testid={`manual-register-${t.id}`} className="btn-outline-gold clip-beveled-sm px-3 py-1.5 text-xs flex items-center gap-1"><UserPlus className="w-3.5 h-3.5" /> Iscrivi Squadra</button>
                              <button onClick={() => navigate("/dashboard/impostazioni", { state: { tid: t.id } })} data-testid={`config-${t.id}`} className="btn-outline-gold clip-beveled-sm px-3 py-1.5 text-xs">Config</button>
                              <button onClick={() => duplicate(t.id)} aria-label={`Duplica ${t.name}`} className="text-[var(--gold)] p-1.5"><Copy className="w-4 h-4" /></button>
                              <button onClick={() => remove(t.id)} data-testid={`delete-tour-${t.id}`} aria-label={`Elimina ${t.name}`} className="text-[var(--destructive)] p-1.5"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
                {archivedTours.length === 0 && (
                  <tr><td colSpan={6} className="p-6 text-center text-[var(--silver)]">Nessun torneo archiviato</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>

      {manualTour && <ManualRegister tournament={manualTour} onClose={() => setManualTour(null)} onDone={load} />}
    </div>
  );
}
